// ActivityPanel.tsx — what he did through connectors (the ledger), what is
// waiting on you, and what he has learned about his own hands (the notes,
// which you can strike).
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface LedgerEntry { at: number; server: string; tool: string; tier: string; args: any; ok: boolean; summary: string; approvedBy?: string; internal: boolean; ms: number }
interface Note { id: string; text: string; bornFrom: string; at: number; before: { calls: number; ok: number }; after: { calls: number; ok: number }; status: string; prunedReason?: string }

const ActivityPanel: React.FC = () => {
  const { token } = useAuth();
  const headers = useCallback((json = false): Record<string, string> => ({ ...(json ? { 'Content-Type': 'application/json' } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }), [token]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [notes, setNotes] = useState<Record<string, Note[]>>({});
  const [error, setError] = useState<string | null>(null);
  const [showPruned, setShowPruned] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [l, n] = await Promise.all([fetch('/api/mcp/ledger?limit=60', { headers: headers() }), fetch('/api/notes', { headers: headers() })]);
      if (l.ok) setLedger(((await l.json()).entries || []).slice().reverse());
      if (n.ok) setNotes((await n.json()).notes || {});
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  }, [headers]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const strike = async (cap: string, id: string) => {
    const why = window.prompt('Why strike this note? (he keeps the reason)') || 'struck by the user';
    await fetch(`/api/notes/${encodeURIComponent(cap)}/${id}/strike`, { method: 'POST', headers: headers(true), body: JSON.stringify({ why }) });
    load();
  };

  const capIds = Object.keys(notes).filter(c => notes[c].some(n => showPruned || n.status === 'active'));

  return (
    <div className="tab-content activity-content">
      <h3>📒 Activity</h3>
      <p className="panel-lede">Everything he did through connectors, and what he has learned about using his hands. This is his record, not a summary.</p>
      {error && <div className="panel-error">{error}</div>}

      <h4>Connector ledger</h4>
      {ledger.length === 0 ? <div className="panel-empty">Nothing yet.</div> : (
        <div className="ledger">
          {ledger.map((e, i) => (
            <div key={i} className={`ledger-row ${e.ok ? 'ok' : 'bad'}`}>
              <span className="ledger-time">{new Date(e.at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
              <span className={`tier tier-${e.tier}`}>{e.tier}</span>
              <span className="ledger-what"><strong>{e.tool}</strong> on {e.server}{e.approvedBy === 'user' ? ' · you approved' : ''}{e.internal ? ' · on his own' : ''}</span>
              <span className="ledger-summary">{e.summary}</span>
            </div>
          ))}
        </div>
      )}

      <h4>What he's learned <button className="link-button" onClick={() => setShowPruned(s => !s)}>{showPruned ? 'hide retired notes' : 'show retired notes'}</button></h4>
      {capIds.length === 0 ? <div className="panel-empty">No notes yet. They come from hand calls that taught him something durable.</div> : capIds.map(cap => (
        <div key={cap} className="notes-cap">
          <div className="notes-cap-title">{cap}</div>
          {notes[cap].filter(n => showPruned || n.status === 'active').map(n => (
            <div key={n.id} className={`note-row ${n.status}`}>
              <div className="note-text">{n.text}</div>
              <div className="note-meta">
                {new Date(n.at).toLocaleDateString()} · from: {n.bornFrom || '—'}
                {n.after.calls >= 5 && <> · since then {n.after.ok}/{n.after.calls} ok{n.before.calls >= 5 && <> (was {Math.round(100 * n.before.ok / n.before.calls)}%)</>}</>}
                {n.status !== 'active' && <> · retired: {n.prunedReason}</>}
              </div>
              {n.status === 'active' && <button className="link-button" onClick={() => strike(cap, n.id)}>strike</button>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default ActivityPanel;
